<template>
    <div>
        <radio-buttons
            :radio-button-list="radioButtonList"
            list-heading="App.RadioButtonListHeader.SelectTopic"
            :default-radio-button="selectedRadioButtonData.labelText"
            :sub-topic="subTopic"
            :select-by-url="true"
            @selected-radio-button="handleSelectedRadioButton"
            @radio-buttons-changed="handleRadioButtonsChanged"
        />
        <area-lists
            v-show="!postLocationsSearchVisible && areaListVisible"
            list-heading="App.RadioButtonListHeader.ValitseAlue"
            @selected-radio-button="handleAreaSelectionRadioButton"
            @selected-list-value="handleAreaSelectionListValue"
        />
        <div
            v-show="locateMeVisible"
            class="search-components"
        >
            <search-address />
            <locate-me />
        </div>
        <checkbox-list
            v-if="postLocationsSearchVisible"
            list-heading="App.CheckboxList.Heading"
            :checkbox-list="checkboxList"
            @checkbox-item="handleCheckboxValue"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import SearchAddress from "@/components/SearchAddress.vue"
    import LocateMeLink from "@/components/LocateMeLink.vue"
    import RadioButtonListWithDropdown from "@/components/RadioButtonListWithDropdown.vue"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import CheckboxList from "@/components/CheckboxList.vue"
    import CheckboxModel from "@/models/checkboxmodel"
    import MunicipalitiesAndProvinces from "@/components/MunicipalitiesAndProvinces.vue"
    import PostiService from "@/services/PostiService"
    import SearchResultModel from "@/models/searchresultmodel"
    import { store } from '@/store/store'

    @Component({
        components: {
            "radio-buttons": RadioButtonListWithDropdown,
            "locate-me": LocateMeLink,
            "search-address": SearchAddress,
            "checkbox-list": CheckboxList,
            "area-lists": MunicipalitiesAndProvinces
        }
    })
    export default class Posti extends Vue {
        @Prop({ type: String }) subTopic!: string

        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/Viestinta/Posti/Postipalvelut/"
        pageName: string = "posti"
        postiService: PostiService = new PostiService()

        radioButtonList: Array<RadioButtonModel> = this.radioButtonListValues

        postLocationsSearchVisible: boolean = true
        areaListVisible: boolean = true
        locateMeVisible: boolean = true

        selectedOfficeTypesFilters: { name: string, dataTypes: number[] }[] = this.officeTypeFilters

        checkboxList: CheckboxModel[] = this.checkboxListValues

        // Valittu radiobutton tietoineen
        selectedRadioButtonData: RadioButtonModel = this.defaultRadioButton

        mounted(): void {
            store.dispatch("setSelectedSubjectRadioButton", this.selectedRadioButtonData)
        }

        async activated(): Promise<void> {
            // tyhjännetään kartan edellinen lokaatiot
            store.dispatch("setStopLocate")
            store.dispatch("setCurrentPage", this.pageName)
            store.dispatch("setLoading", { name: this.pageName, value: true })
            await this.getInfokartat()
            this.radioButtonList = this.radioButtonListValues
            this.selectedRadioButtonData = this.defaultRadioButton
            store.dispatch("setSelectedSubjectRadioButton", this.selectedRadioButtonData)

            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", "PostiHaeToimipaikkoja")

            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)

            store.dispatch("setLoading", { name: this.pageName, value: false })
        }

        deactivated() {
            this.radioButtonList = []
            store.dispatch("setSubTopic", null)
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSelectedAreaRadioButton", "")
            store.dispatch("setSelectedMunicipality", "")
            store.dispatch("setSelectedProvince", "")
            store.dispatch("setInfokarttaRadioValues", [])
        }

        async getInfokartat() {
            const infokartatData = store.getters.postiInfokarttaRadioValues
            store.dispatch("setInfokarttaRadioValues", infokartatData)
        }

        get radioButtonListValues(): RadioButtonModel[] {
            const list: RadioButtonModel[] = [
                new RadioButtonModel("App.RadioList.SearchLocations", "PostiHaeToimipaikkoja"),
                new RadioButtonModel("App.RadioList.NumberOfServicePoints", "PostiTarkasteleMaaraa")]

            // haetaan posti tyyppiset infokartat
            const radioButtonListValues = store.getters.infokarttaRadioValues ?? new RadioButtonModel("", "")

            if (radioButtonListValues) {
                radioButtonListValues.forEach(element => {
                    if (this.activeLang.toString() === "fi") {
                        list.push(new RadioButtonModel(element.nimifi, element.tunniste, "Infokartat"))
                    }
                    else if (this.activeLang.toString() === "sv") {
                        list.push(new RadioButtonModel(element.nimisv, element.tunniste, "Infokartat"))
                    }
                    else {
                        list.push(new RadioButtonModel(element.nimien, element.tunniste, "Infokartat"))
                    }
                })
            }

            return list ?? [new RadioButtonModel("", "")]
        }

        get leftSlotHeaderPosti(): string | undefined {
            return this.$t("App.LeftSlot.Header.Posti")
        }

        get officeTypeFilters(): { name: string, dataTypes: number[] }[] {
            // filtterit: "Postitoimipiste", "Noutopiste", "Postilokero", "Pakettiautomaatti", "Kirjelaatikko", "Yrityspiste"
            const postitoimipisteet = this.postiService.getPostitoimipisteet()
            const filters: { name: string, dataTypes: number[] }[] = []
            for (let i = 0; i < postitoimipisteet.length; i++) {
                filters.push({ name: postitoimipisteet[i].name, dataTypes: postitoimipisteet[i].dataTyypit })
            }
            return filters
        }

        get checkboxListValues(): CheckboxModel[] {
            const postitoimipisteet = this.postiService.getPostitoimipisteet()
            const values: CheckboxModel[] = []
            for (let i = 0; i < postitoimipisteet.length; i++) {
                const kielitermi = "App.Posti.CheckboxList." + postitoimipisteet[i].languageTerm
                const piste = new CheckboxModel(kielitermi, postitoimipisteet[i].name, true)
                values.push(piste)
            }
            return values
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // kielivalinta muuttunut. nyt pitäisi lataa data uudestaan ja käyttäjän tekemä mahdollinen valinta....
            if (this.currentPage !== this.pageName) {
                return
            }
            this.updateData(store.getters.rightSlot)
        }

        get defaultRadioButton(): RadioButtonModel {
            // Jos sivulle tullaan subTopic routen kautta, niin asetetaan default radiobutton value routen mukaisesti
            if (this.subTopic) {
                for (let i = 0; i < this.radioButtonList.length; i++) {
                    if (this.radioButtonList[i].radioValue === this.subTopic) {
                        return this.radioButtonList[i]
                    }
                }
            }
            return this.radioButtonList[0]
        }

        async handleSelectedRadioButton(item: RadioButtonModel) {
            this.selectedRadioButtonData = item
            this.updatePostLocationsSearchVisible(item)
            store.dispatch("setSelectedSubjectRadioButton", item)
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        updatePostLocationsSearchVisible(item: RadioButtonModel) {
            if (item.radioValue === "PostiHaeToimipaikkoja") {
                this.postLocationsSearchVisible = true
                this.areaListVisible = false
                this.locateMeVisible = true
            }
            else if (item.radioValue === "PostiTarkasteleMaaraa") {
                this.postLocationsSearchVisible = false
                this.areaListVisible = true
                this.locateMeVisible = false
            }

            // haetaan posti tyyppiset infokartat
            const radioButtonListValues = store.getters.infokarttaRadioValues ?? new RadioButtonModel("", "")

            if (radioButtonListValues) {
                radioButtonListValues.forEach(element => {
                    if (item.radioValue === element.tunniste) {
                        this.postLocationsSearchVisible = false
                        this.areaListVisible = false
                        this.locateMeVisible = true
                    }
                })
            }
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        handleAreaSelectionRadioButton(item: RadioButtonModel) {
            store.dispatch("setSelectedAreaRadioButton", String(item))
            this.updateData(store.getters.rightSlot)
        }

        handleAreaSelectionListValue(item: string) {
            // Valittu kunta tai maakunta dropdownista
            if (store.getters.selectedAreaRadioButton === "kunta") {
                store.dispatch("setSelectedMunicipality", item)
            }
            else if (store.getters.selectedAreaRadioButton === "maakunta") {
                store.dispatch("setSelectedProvince", item)
            }
        }

        handleRadioButtonsChanged(list: Array<RadioButtonModel>) {
            // Päivitä defaultina valittu radio button
            this.selectedRadioButtonData = list[0]
            this.updatePostLocationsSearchVisible(this.selectedRadioButtonData)
        }

        async handleCheckboxValue(item: CheckboxModel) {
            // palvelupistetyyppien filttereiden asettaminen
            this.setOfficeTypesFilters(item)
            this.setDataToLoad(this.selectedRadioButtonData)
        }

        async setOfficeTypesFilters(item: CheckboxModel) {
            const filter = this.selectedOfficeTypesFilters.find(x => x.name === item.checkboxValue)
            if (filter) {
                const index = this.selectedOfficeTypesFilters.indexOf(filter)

                if (index === -1) {
                    const originalFilters = this.officeTypeFilters
                    originalFilters.find((f) => {
                        if (f.name === item.checkboxValue) {
                            this.selectedOfficeTypesFilters.push({ name: f.name, dataTypes: f.dataTypes })
                            return true
                        }
                        return false
                    })
                }
                else {
                    this.selectedOfficeTypesFilters.splice(index, 1)
                }
            }
            // TODO: Mitäs käyttäjä poistaa kaikki täpät? tyhjennetäänkö kartta? vai pitäisikö checkboxlistissä olla ominaisuus jolla estetään viimeisen valinnan poistaminen?
        }

        async setDataToLoad(selectedRadio: RadioButtonModel): Promise<void> {
            if (selectedRadio !== this.selectedRadioButtonData) {
                return
            }

            if (this.currentPage !== this.pageName) {
                return
            }

            store.dispatch("setLoading", { name: "posti", value: true })

            this.selectedRadioButtonData = selectedRadio

            this.updateData(selectedRadio.radioValue)
        }

        async updateData(radioValue: string): Promise<void> {
            store.dispatch("setLoading", { name: "posti", value: true })

            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", this.selectedRadioButtonData.groupName ?? this.selectedRadioButtonData.radioValue)

            const clearLocationMarker = store.getters.currentPage.toLowerCase() !== store.getters.dataUrlToLoad?.url?.split("/")[2]?.toLowerCase()

            this.updatePostLocationsSearchVisible(this.selectedRadioButtonData)

            if (radioValue === "PostiTarkasteleMaaraa") {
                store.dispatch("setRightSlot", "PostiTarkasteleMaaraa")
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "PostipalvelutKuntakohtainen", type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "PostipalvelutMaakuntakohtainen", type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "PostipalvelutKokoSuomi", type: radioValue, clearLocationMarker })
                }
            }
            else if (radioValue === "PostiHaeToimipaikkoja") {
                // Rightslotin turha päivittäminen aiheuttaa julkaisuaikatekstin välkkymistä
                if (store.getters.rightSlot !== "PostiHaeToimipaikkoja") {
                    store.dispatch("setRightSlot", "PostiHaeToimipaikkoja")
                }

                // rajapinnassa tyyppi muuttuja joka on int,
                // jos tyyppi tyhjä niin haetaan kaikki erityyppiset postipalvelut.
                // Tyyppi rajauksena voi olla  1 = "ToimipisteOma", 2 = "ToimipisteAsiamiesposti", 3 = "PakettiautomaattiSmartPost", 4 = "Noutopiste", 5 = "Yrityspiste", 6 = "KirjelaatikkoTavallinen",
                // 7 = "KirjelaatikkoMyohemminTyhjennettava", 8 = "KirjelaatikkoErikoisleimaus", 9 = "Postilokero" tai 10 = "Lahilokero"
                if (this.selectedOfficeTypesFilters.length === this.postiService.getTyyppiLkm()) {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart, type: radioValue, clearLocationMarker })
                }
                else {
                    let filter = new URLSearchParams()
                    for (let i = 0; i < this.selectedOfficeTypesFilters.length; i++) {
                        const dataTypes: number[] = this.selectedOfficeTypesFilters[i].dataTypes
                        for (let i = 0; i < dataTypes.length; i++) {
                            filter.append("tyyppi", dataTypes[i].toString())
                        }
                    }
                    // ei mitään valittuna joten rajapintaa kutsu joka palauttaa tyhjää (purkka ruudun tyhjentämiseksi)
                    if (this.selectedOfficeTypesFilters.length === 0) {
                        filter = new URLSearchParams()
                        filter.append("tyyppi", "-1")
                        // TODO yleiskutsu kartan tyhjentämiseksi?
                    }
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart, type: radioValue, params: filter, clearLocationMarker })
                }
            }
            else if (this.selectedRadioButtonData?.groupName !== null && this.selectedRadioButtonData?.groupName === "Infokartat") {
                const infokartatApiCallBasePart: string = "v1/Infokartta/"

                store.dispatch("setDataUrlToLoad", { url: infokartatApiCallBasePart + "Infokartta" + "?tunniste=" + this.selectedRadioButtonData?.radioValue, type: "Infokartta", clearLocationMarker })
            }

            store.dispatch("setLoading", { name: "posti", value: false })
        }
    }
</script>
